import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import { useCurrencyFormatter } from 'hooks/useNumberFormatters'
import { EXP_CATEGORY_LUX_PLUS_ID, EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID } from 'constants/experience'
import { getMatchingFilterFromRange } from 'lib/experiences/experienceSearchFilterUtils'
import { groupBy, without } from 'lib/array/arrayUtils'
import useQueryParams from 'hooks/useQueryParams'
import useLoyaltyFilterLabels from 'hooks/Search/useLoyaltyFilterLabels'
import useExperiencesSearchFilters from './useExperiencesSearchFilters'
import {
  EXP_CATEGORY_TRANSFERS_AND_LOUNGES_REAL_CATEGORY_IDS,
  getTravelEssentialsRealCategoryIds,
} from '../ExperiencesSearch/experienceSearchTravelEssentialsSplit'
import useTrackExperienceSearchFilter from './useTrackExperienceSearchFilter'
import { getSearchThemeTitle } from './useExperienceThemes'

export interface ActiveFilterChip {
  key: string
  label: string
  onRemove: () => void
}

const LUX_PLUS_CATEGORY_IDS = [EXP_CATEGORY_LUX_PLUS_ID, EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID]

function hasParentCategoryId(
  category: App.ExperienceItemCategory,
): category is App.ExperienceItemCategory & { parentId: number } {
  return typeof category.parentId === 'number'
}

function useActiveExperienceFilterChips(onAfterDateRemove?: () => void): Array<ActiveFilterChip> {
  const intl = useIntl()
  const navigate = useNavigate()
  const currencyFormatter = useCurrencyFormatter()
  const queryParams = useQueryParams()
  const { searchFilters, setSearchFilters, setCategoryCodesSearchFilter } = useExperiencesSearchFilters()
  const trackSearchFilter = useTrackExperienceSearchFilter()
  const { getLabel: getCampaignFilterLabel } = useLoyaltyFilterLabels()
  const categories = useAppSelector(state => state.experience.experienceCategories)
  const experienceListFacets = useAppSelector(state => state.experience.experienceListFacets)

  const categoryNameById = useMemo(() => {
    const map = new Map<number, string>()
    for (const category of categories) {
      map.set(category.id, category.name)
    }
    return map
  }, [categories])

  const childCategoryIdsByParentId = useMemo(() => {
    return groupBy(
      categories.filter(hasParentCategoryId),
      (category) => category.parentId,
      (category) => category.id,
    )
  }, [categories])

  const travelEssentialsRealCategoryIds = useMemo(
    () => getTravelEssentialsRealCategoryIds(categories),
    [categories],
  )

  const collectionNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const facetState of Object.values(experienceListFacets)) {
      for (const collection of facetState?.facets?.collection ?? []) {
        map.set(collection.collectionId, collection.theme)
      }
    }
    return map
  }, [experienceListFacets])

  const areaNameByPlaceId = useMemo(() => {
    const map = new Map<string, string>()
    for (const facetState of Object.values(experienceListFacets)) {
      for (const area of facetState?.facets?.areas ?? []) {
        map.set(area.placeId, area.name)
      }
    }
    return map
  }, [experienceListFacets])

  const categoryFacetCountById = useMemo(() => {
    const map = new Map<number, number>()
    for (const facetState of Object.values(experienceListFacets)) {
      for (const category of facetState?.facets?.category ?? []) {
        map.set(category.categoryId, category.count)
      }
    }
    return map
  }, [experienceListFacets])

  return useMemo(() => {
    const chips: Array<ActiveFilterChip> = []
    // Travel Essentials remains a real taxonomy category. This chip aggregates
    // the virtual Transfers/Lounges split used on the search page.
    const transfersAndLoungesCategoryIds = new Set([
      ...EXP_CATEGORY_TRANSFERS_AND_LOUNGES_REAL_CATEGORY_IDS,
      ...travelEssentialsRealCategoryIds,
    ])
    const destinationId = queryParams.get('destinationId')
    const destinationName = queryParams.get('destinationName')

    if (
      destinationId &&
      destinationName &&
      areaNameByPlaceId.has(destinationId)
    ) {
      const label = areaNameByPlaceId.get(destinationId) ?? destinationName
      chips.push({
        key: `area-${destinationId}`,
        label,
        onRemove: () => {
          trackSearchFilter({
            label,
            type: 'area',
            value: 'cleared',
          })
          const nextQueryParams = new URLSearchParams(queryParams)
          const parentDestinationId = queryParams.get('areaParentDestinationId')
          const parentDestinationName = queryParams.get('areaParentDestinationName')
          if (parentDestinationId && parentDestinationName) {
            nextQueryParams.set('destinationId', parentDestinationId)
            nextQueryParams.set('destinationName', parentDestinationName)
          } else {
            nextQueryParams.delete('destinationId')
            nextQueryParams.delete('destinationName')
          }
          nextQueryParams.delete('areaParentDestinationId')
          nextQueryParams.delete('areaParentDestinationName')
          navigate({ search: nextQueryParams.toString() }, { replace: true })
        },
      })
    }

    const activeDateFilter = searchFilters.date
      ?? getMatchingFilterFromRange(searchFilters.checkIn, searchFilters.checkOut)
    if (activeDateFilter && activeDateFilter !== 'anytime') {
      const dateLabels: Partial<Record<string, string>> = {
        today: intl.formatMessage({
          id: 'AHwE-JrMwGjmJsScIJJ8',
          defaultMessage: 'Today',
          description: 'Quick date chip label for today in experiences date dropdown',
        }),
        tomorrow: intl.formatMessage({
          id: 'IdXFYyn3Ih3xMMiAMWJ1',
          defaultMessage: 'Tomorrow',
          description: 'Quick date chip label for tomorrow in experiences date dropdown',
        }),
        'this-weekend': intl.formatMessage({
          id: 'AzBOEGuHa1-HP1JDqYkD',
          defaultMessage: 'This weekend',
          description: 'Quick date chip label for this weekend in experiences date dropdown',
        }),
      }
      const label = dateLabels[activeDateFilter]
      if (label) {
        chips.push({
          key: `date-${activeDateFilter}`,
          label,
          onRemove: () => {
            trackSearchFilter({
              label,
              type: 'date',
              value: 'cleared',
            })
            setSearchFilters({
              ...searchFilters,
              date: undefined,
              checkIn: undefined,
              checkOut: undefined,
            })
            onAfterDateRemove?.()
          },
        })
      }
    }

    if (searchFilters.minRating) {
      chips.push({
        key: `rating-${searchFilters.minRating}`,
        label: intl.formatMessage(
          {
            id: '1ibx0Pa6PegLZuMET_08',
            defaultMessage: '{rating}.0+ rating',
            description: 'Rating filter chip label showing minimum rating threshold in experience search',
          },
          { rating: searchFilters.minRating },
        ),
        onRemove: () => {
          trackSearchFilter({
            label: 'Customer rating',
            type: 'customer_rating',
            value: 'cleared',
          })
          setSearchFilters({ ...searchFilters, minRating: undefined })
        },
      })
    }

    if (searchFilters.priceGte || searchFilters.priceLte) {
      const parts: Array<string> = []
      if (searchFilters.priceGte) {
        parts.push(intl.formatMessage(
          {
            id: 'JX7yTDLY6quhbUH-HQ9h',
            defaultMessage: '{price}+',
            description: 'Price filter chip label showing minimum price in experience search',
          },
          { price: currencyFormatter(searchFilters.priceGte) },
        ))
      }
      if (searchFilters.priceLte) {
        parts.push(intl.formatMessage(
          {
            id: 'I7GjIDDs0Ut5Uzu4a5EA',
            defaultMessage: 'up to {price}',
            description: 'Price filter chip label showing maximum price in experience search',
          },
          { price: currencyFormatter(searchFilters.priceLte) },
        ))
      }
      chips.push({
        key: 'price',
        label: parts.join(' '),
        onRemove: () => {
          trackSearchFilter({
            label: 'Price',
            type: 'price',
            value: 'cleared',
          })
          setSearchFilters({
            ...searchFilters,
            priceGte: undefined,
            priceLte: undefined,
          })
        },
      })
    }

    const selectedCategoryCodes = searchFilters.categoryCodes ?? []
    const hasTransfersAndLoungesCategory = selectedCategoryCodes.some(
      id => transfersAndLoungesCategoryIds.has(id),
    )

    if (hasTransfersAndLoungesCategory) {
      const label = intl.formatMessage({
        id: 'LXwK9XbUBWiYdMnvjHn6',
        defaultMessage: 'Transfers and Lounges',
        description: 'Active filter chip label for transfer and lounge experience categories',
      })
      chips.push({
        key: 'category-transfers-and-lounges',
        label,
        onRemove: () => {
          trackSearchFilter({
            label,
            type: 'category',
            value: 'cleared',
          })
          setCategoryCodesSearchFilter(selectedCategoryCodes.filter(id => !transfersAndLoungesCategoryIds.has(id)))
        },
      })
    }

    const groupedCategoryIds = new Set<number>()
    const selectedCategoryIdSet = new Set(selectedCategoryCodes)

    for (const [parentId, childIds] of childCategoryIdsByParentId.entries()) {
      if ([parentId, ...childIds].some(id => transfersAndLoungesCategoryIds.has(id))) {
        continue
      }

      const selectableChildIds = childIds.filter(id => {
        if (LUX_PLUS_CATEGORY_IDS.includes(id)) {
          return false
        }

        if (categoryFacetCountById.size === 0) {
          return true
        }

        return (categoryFacetCountById.get(id) ?? 0) > 0 ||
          selectedCategoryIdSet.has(id)
      })
      const hasCompleteParentSelection =
        selectableChildIds.length > 0 &&
        selectableChildIds.every(id => selectedCategoryIdSet.has(id))

      if (!hasCompleteParentSelection) {
        continue
      }

      const parentName = categoryNameById.get(parentId)
      const name = parentName ? getSearchThemeTitle(intl, parentId, parentName) : undefined
      if (!name) {
        continue
      }

      const relatedCategoryIds = [parentId, ...selectableChildIds]
      relatedCategoryIds.forEach(id => groupedCategoryIds.add(id))
      chips.push({
        key: `category-${parentId}`,
        label: name,
        onRemove: () => {
          trackSearchFilter({
            label: name,
            type: 'category',
            value: 'cleared',
          })
          setCategoryCodesSearchFilter(
            (searchFilters.categoryCodes ?? []).filter(id => !relatedCategoryIds.includes(id)),
          )
        },
      })
    }

    selectedCategoryCodes
      ?.filter(id => !LUX_PLUS_CATEGORY_IDS.includes(id))
      .filter(id => !transfersAndLoungesCategoryIds.has(id))
      .filter(id => !groupedCategoryIds.has(id))
      .forEach(id => {
        const categoryName = categoryNameById.get(id)
        const name = categoryName ? getSearchThemeTitle(intl, id, categoryName) : undefined
        if (name) {
          chips.push({
            key: `category-${id}`,
            label: name,
            onRemove: () => {
              trackSearchFilter({
                label: name,
                type: 'category',
                value: 'cleared',
              })
              setCategoryCodesSearchFilter(without(searchFilters.categoryCodes ?? [], id))
            },
          })
        }
      })

    searchFilters.collections?.forEach(collectionId => {
      const label = collectionNameById.get(collectionId) ?? collectionId
      chips.push({
        key: `collection-${collectionId}`,
        label,
        onRemove: () => {
          trackSearchFilter({
            label,
            type: 'theme_collection',
            value: 'cleared',
          })
          const nextCollections = without(searchFilters.collections ?? [], collectionId)
          setSearchFilters({
            ...searchFilters,
            collections: nextCollections.length ? nextCollections : undefined,
          })
        },
      })
    })

    searchFilters.campaigns?.forEach(campaign => {
      const label = getCampaignFilterLabel(campaign)
      chips.push({
        key: `campaign-${campaign}`,
        label,
        onRemove: () => {
          trackSearchFilter({
            label,
            type: 'campaign',
            value: 'cleared',
          })
          const nextCampaigns = without(searchFilters.campaigns ?? [], campaign)
          setSearchFilters({
            ...searchFilters,
            campaigns: nextCampaigns.length ? nextCampaigns : undefined,
          })
        },
      })
    })

    const featuredLabels: Partial<Record<Experiences.FeaturedFilter, string>> = {
      'free-cancellation': intl.formatMessage({
        id: 'gnc42bYCUYEC1VtZ_FBb',
        defaultMessage: 'Free cancellation',
        description: 'Form field label for free cancellation policy on experience',
      }),
      'instant-confirmation': intl.formatMessage({
        id: 'liiEI8aOkzbI4FNe-4v4',
        defaultMessage: 'Instant confirmation',
        description: 'Featured filter option for instant confirmation in experience search',
      }),
      'is-gift': intl.formatMessage({
        id: 'kl0s-nwUJycMsemZfm-u',
        defaultMessage: 'Gift inspiration',
        description: 'Featured filter chip label for gift inspiration in experience search',
      }),
    }
    searchFilters.featured?.forEach(featured => {
      chips.push({
        key: `featured-${featured}`,
        label: featuredLabels[featured] ?? featured,
        onRemove: () => {
          trackSearchFilter({
            label: featuredLabels[featured] ?? featured,
            type: 'featured',
            value: 'cleared',
          })
          setSearchFilters({
            ...searchFilters,
            featured: searchFilters.featured?.filter(featuredFilter => featuredFilter !== featured),
          })
        },
      })
    })

    const timeSlotLabels: Record<Experiences.TimeSlotFilter, string> = {
      'all-day': intl.formatMessage({
        id: 'Pf0P5fL9eSyXHrJQQe0k',
        defaultMessage: 'All day',
        description: 'Time slot filter chip for experiences that can start at any time of day',
      }),
      morning: intl.formatMessage({
        id: 'QG2XM-mi9lejKz3_md6R',
        defaultMessage: 'Morning (6am–12pm)',
        description: 'Time slot filter option for experiences starting in the morning',
      }),
      afternoon: intl.formatMessage({
        id: 'pPyZGRpHaRBFzGE_UzfA',
        defaultMessage: 'Afternoon (12pm–6pm)',
        description: 'Time slot filter option for experiences starting in the afternoon',
      }),
      evening: intl.formatMessage({
        id: 'yW1nHz16fgyJSenhi45Q',
        defaultMessage: 'Evening (6pm+)',
        description: 'Time slot filter option for experiences starting in the evening',
      }),
    }
    searchFilters.timeSlots?.forEach(timeSlot => {
      const label = timeSlotLabels[timeSlot] ?? timeSlot
      chips.push({
        key: `time-slot-${timeSlot}`,
        label,
        onRemove: () => {
          trackSearchFilter({
            label,
            type: 'starting_time',
            value: 'cleared',
          })
          const nextTimeSlots = searchFilters.timeSlots?.filter(slot => slot !== timeSlot)
          setSearchFilters({
            ...searchFilters,
            timeSlots: nextTimeSlots?.length ? nextTimeSlots : undefined,
          })
        },
      })
    })

    if (searchFilters.leExclusive) {
      const label = intl.formatMessage({
        id: '-PmTBOAd00zapB4PVD4L',
        defaultMessage: 'Promotions & Discounts',
        description:
          'Checkbox label for the Luxury Escapes promotions and discounts experience search filter',
      })

      chips.push({
        key: 'le-exclusive',
        label,
        onRemove: () => {
          trackSearchFilter({
            label,
            type: 'le_exclusive',
            value: 'cleared',
          })
          setSearchFilters({ ...searchFilters, leExclusive: undefined })
        },
      })
    }

    if (searchFilters.luxPlusFeatures?.length) {
      chips.push({
        key: 'luxplus',
        label: 'Lux Plus',
        onRemove: () => {
          trackSearchFilter({
            label: 'Lux Plus',
            type: 'lux_plus',
            value: 'cleared',
          })
          setSearchFilters({ ...searchFilters, luxPlusFeatures: undefined })
        },
      })
    }

    if (searchFilters.viewPriceInPoints) {
      chips.push({
        key: 'view-price-in-points',
        label: intl.formatMessage({
          id: 'Pl7ew0Wylbx4ZUv7DdgF',
          defaultMessage: 'View Price in Points',
          description: 'Checkbox label to show experience search prices as Société points only',
        }),
        onRemove: () => {
          trackSearchFilter({
            label: 'View Price in Points',
            type: 'view_price_in_points',
            value: 'cleared',
          })
          setSearchFilters({ ...searchFilters, viewPriceInPoints: undefined })
        },
      })
    }

    const travelStyleLabels: Partial<Record<Experiences.TravelStyleFilter, string>> = {
      'family-friendly': intl.formatMessage({
        id: 'A2Rii617VkKkEqZgSTXB',
        defaultMessage: 'Family friendly',
        description: 'Travel style filter option for family friendly in experience search',
      }),
      'couples': intl.formatMessage({
        id: 'LaA0PgRiBlfdYL43-IMC',
        defaultMessage: 'Couples',
        description: 'Traveller type filter label for couples in the search or listing filters panel',
      }),
      'large-groups': intl.formatMessage({
        id: 'lJH8OZuJxWjWNnh4laP_',
        defaultMessage: 'Large groups',
        description: 'Travel style filter option for large groups in experience search',
      }),
      'solo-explorers': intl.formatMessage({
        id: 'ymauUvM_REtis5IuuLHz',
        defaultMessage: 'Solo explorers',
        description: 'Travel style filter option for solo explorers in experience search',
      }),
    }
    searchFilters.travelStyles?.forEach(travelStyle => {
      chips.push({
        key: `style-${travelStyle}`,
        label: travelStyleLabels[travelStyle] ?? travelStyle,
        onRemove: () => {
          trackSearchFilter({
            label: travelStyleLabels[travelStyle] ?? travelStyle,
            type: 'travel_style',
            value: 'cleared',
          })
          setSearchFilters({
            ...searchFilters,
            travelStyles: searchFilters.travelStyles?.filter(s => s !== travelStyle),
          })
        },
      })
    })

    return chips
  }, [
    searchFilters,
    setSearchFilters,
    setCategoryCodesSearchFilter,
    navigate,
    areaNameByPlaceId,
    categoryFacetCountById,
    categoryNameById,
    childCategoryIdsByParentId,
    collectionNameById,
    getCampaignFilterLabel,
    intl,
    currencyFormatter,
    onAfterDateRemove,
    queryParams,
    trackSearchFilter,
    travelEssentialsRealCategoryIds,
  ])
}

export default useActiveExperienceFilterChips
